import SectionHeader from "@/components/feature/panel/SectionHeader";
import EmptyState from "@/components/feature/panel/EmptyState";
import PanelLinks from "@/components/feature/panel/PanelLinks";
import { getDataClient } from "@/lib/dataClient";
import { useDataQuery } from "@/hooks/useDataQuery";

interface RoleCount {
  role: string;
  count: number;
}

const ROLE_META: Record<string, { label: string; icon: string }> = {
  admin: { label: "Yönetici", icon: "ri-shield-star-line" },
  ogretmen: { label: "Öğretmen", icon: "ri-user-star-line" },
  ogrenci: { label: "Öğrenci", icon: "ri-graduation-cap-line" },
  veli: { label: "Veli", icon: "ri-heart-3-line" },
};

export default function AdminRoller() {
  const { data, loading, error, reload } = useDataQuery<RoleCount[]>(async () => {
    const dataClient = getDataClient();
    const { data, error } = await dataClient.from("profiles").select("role");
    if (error) throw error;
    const counts = new Map<string, number>();
    (data ?? []).forEach((p: { role: string | null }) => {
      const r = p.role ?? "bilinmeyen";
      counts.set(r, (counts.get(r) ?? 0) + 1);
    });
    return Array.from(counts.entries()).map(([role, count]) => ({ role, count }));
  });

  const total = data?.reduce((sum, r) => sum + r.count, 0) ?? 0;

  const roleCards = Object.entries(ROLE_META).map(([role, meta]) => {
    const found = data?.find((r) => r.role === role);
    return { role, ...meta, count: found?.count ?? 0 };
  });

  return (
    <div className="space-y-6">
      <SectionHeader
        title="Roller ve Yetkiler"
        description="Sistemdeki rollerin kullanıcı dağılımını görüntüle."
      />

      {loading ? (
        <EmptyState icon="ri-loader-4-line" title="Yükleniyor..." />
      ) : error ? (
        <div className="rounded-lg border border-background-200 bg-background-50 p-6 text-center">
          <p className="text-sm text-foreground-600">{error}</p>
          <button
            type="button"
            onClick={reload}
            className="mt-4 inline-flex items-center gap-2 whitespace-nowrap px-4 py-2 rounded-md text-sm font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors"
          >
            <i className="ri-refresh-line" aria-hidden="true"></i>
            Tekrar dene
          </button>
        </div>
      ) : total === 0 ? (
        <EmptyState
          icon="ri-shield-keyhole-line"
          title="Henüz kullanıcı kaydı yok"
          description="Kullanıcılar kaydoldukça rol dağılımı burada görünür."
        />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {roleCards.map(({ role, label, icon, count }) => {
            return (
              <div key={role} className="rounded-lg border border-background-200 bg-background-50 p-5 flex items-center gap-3">
                <span className="w-11 h-11 flex items-center justify-center rounded-md bg-primary-100 text-primary-700 shrink-0">
                  <i className={`${icon} text-lg`} aria-hidden="true"></i>
                </span>
                <div className="min-w-0">
                  <p className="font-heading font-semibold text-base text-foreground-950">{label}</p>
                  <p className="text-sm text-foreground-500">{count} kullanıcı</p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <PanelLinks
        links={[
          { label: "Kullanıcılar", href: "/yonetici/kullanicilar", icon: "ri-group-line" },
          { label: "Denetim Kaydı", href: "/yonetici/denetim-kaydi", icon: "ri-history-line" },
        ]}
      />
    </div>
  );
}